import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";

const URL = "https://www.mostrandomexico.mx/blog/cdmx";
const TITLE = "Qué hacer en Ciudad de México: guía completa 2026";
const DESCRIPTION =
  "Guía práctica de qué hacer en Ciudad de México: museos, gastronomía, barrios imperdibles y los mejores tours para vivir CDMX como un local.";

export const Route = createFileRoute("/blog_/cdmx")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "article" },
      { property: "og:url", content: URL },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: TITLE,
          description: DESCRIPTION,
          author: { "@type": "Organization", name: "Mostrando México" },
          publisher: { "@type": "Organization", name: "Mostrando México" },
          mainEntityOfPage: URL,
        }),
      },
    ],
  }),
  component: CdmxGuide,
});

function CdmxGuide() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
        <header className="mb-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">
            Guía de viaje · Ciudad de México
          </p>
          <h1 className="mt-3 font-display text-4xl font-semibold leading-tight sm:text-5xl">
            Qué hacer en Ciudad de México
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            CDMX es una de las capitales culturales más vibrantes del mundo: pirámides
            prehispánicas, museos de talla mundial, mercados gastronómicos y barrios para
            perderse. Esta guía reúne lo esencial para tu primer viaje (o el quinto).
          </p>
        </header>

        <section className="prose prose-neutral max-w-none space-y-6">
          <h2 className="font-display text-2xl font-semibold">1. Centro Histórico</h2>
          <p>
            Comienza en el Zócalo, una de las plazas más grandes del mundo. Visita la
            Catedral Metropolitana, el Templo Mayor y el Palacio Nacional con los murales
            de Diego Rivera. Termina en Bellas Aletes y la Alameda Central.
          </p>

          <h2 className="font-display text-2xl font-semibold">2. Teotihuacán</h2>
          <p>
            A 50 km de la ciudad, las pirámides del Sol y la Luna son imperdibles.
            Los tours organizados incluyen transporte, guía certificado y suelen
            combinar la visita con la Basílica de Guadalupe.
          </p>
          <p>
            <Link to="/tours/$city" params={{ city: "ciudad-de-mexico" }} className="text-primary font-semibold hover:underline">
              Ver tours a Teotihuacán y CDMX →
            </Link>
          </p>

          <h2 className="font-display text-2xl font-semibold">3. Coyoacán y Frida Kahlo</h2>
          <p>
            La Casa Azul, hogar de Frida Kahlo, es uno de los museos más visitados de México.
            Reserva con anticipación. Coyoacán es perfecto para caminar, comer churros y
            tomar mezcal por la tarde.
          </p>

          <h2 className="font-display text-2xl font-semibold">4. Xochimilco</h2>
          <p>
            Los canales y trajineras coloridas son patrimonio de la humanidad. Combínalo
            con una visita al Museo Dolores Olmedo o un recorrido gastronómico por las
            chinampas.
          </p>

          <h2 className="font-display text-2xl font-semibold">5. Comer en CDMX</h2>
          <p>
            Tacos al pastor en El Tizoncito, mole en San Ángel, mercado de San Juan para
            ingredientes exóticos, y barrio Roma-Condesa para alta cocina mexicana
            contemporánea.
          </p>

          <h2 className="font-display text-2xl font-semibold">6. Museos imperdibles</h2>
          <ul className="list-disc pl-6">
            <li>Museo Nacional de Antropología</li>
            <li>Museo Soumaya</li>
            <li>Museo Jumex</li>
            <li>Palacio de Bellas Artes</li>
          </ul>

          <h2 className="font-display text-2xl font-semibold">Reserva tu experiencia</h2>
          <p>
            Explora todos los tours operados por proveedores locales certificados en
            nuestro catálogo de Ciudad de México.
          </p>
          <p>
            <Link
              to="/tours/$city"
              params={{ city: "ciudad-de-mexico" }}
              className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
            >
              Ver tours en Ciudad de México
            </Link>
          </p>
        </section>
      </article>
      <SiteFooter />
    </div>
  );
}
